import {
  AudienceFuelAndConvenienceProfileTargetingAttributeFragment,
  Category_Restrictions,
  Fuel_And_Convenience_Profile_Attribute as Attribute,
  Fuel_And_Convenience_Profile_Operator as Operator,
} from '@occc/graphql';
import { GroupAudienceTargetingAttributesByType } from '@occc-app/dataAccessLayer/audience/utils';
import { routeCreator } from '@occc-app/routes/Audience/utils';

import {
  TargetingAttributes,
  FuelAndConvenienceProfile,
  Purchase,
  Segment,
  Fulfillment,
  OfferActivation,
  WalletSteeringAttribute,
  ProductInterestsAttribute,
  QualifiedEventAttribute,
  EmployeeTargetingAttributes,
  Survey,
  CombinedAudiencesAttribute,
} from '../types';
import { acquisitionChannelItems } from '../components/FuelAndConvenienceProfile/constants';
import { FuelAndConvenienceProfileValues } from '../components/FuelAndConvenienceProfile/types';

export const getAudiencePageHeaderLinks = (params: { audienceName: string }) => [
  {
    to: routeCreator.base(),
    label: 'Audiences',
  },
  {
    label: params.audienceName,
  },
];

export const getAudienceExportPageHeaderLinks = (params: {
  audienceName: string;
  audienceId: string;
}) => [
  {
    to: routeCreator.base(),
    label: 'Audiences',
  },
  {
    to: routeCreator.details(params.audienceId),
    label: params.audienceName,
  },
  {
    label: 'Export',
  },
];

const attributeMap: Partial<{ [key in Attribute]: string }> = {
  [Attribute.AcquisitionChannel]: 'Acquired by channels',
  [Attribute.RestrictionVerification]: 'EAIV Restrictions',
  [Attribute.HasAch]: 'Enrolled in ACH',
};

const categoryRestrictionTitle: Record<Category_Restrictions, string> = {
  [Category_Restrictions.Age]: 'Age',
  [Category_Restrictions.Eaiv]: 'EAIV',
};

const getAcquisitionChannelValues = (target?: string[] | null): string => {
  if (target?.length) {
    const acquisitionChannelLabels = target.map((item: string) => {
      const index = acquisitionChannelItems.findIndex(
        (acquisitionChannel) => acquisitionChannel.value === item,
      );

      return acquisitionChannelItems[index].label;
    });

    return acquisitionChannelLabels.join(', ');
  }

  return 'No specified channels';
};

type PrepareFuelAndConvenienceProfileAttribute = (
  criteria: AudienceFuelAndConvenienceProfileTargetingAttributeFragment['criteria'],
) => FuelAndConvenienceProfile;

const getAttributeValue = (
  attribute: Attribute,
  operator: Operator,
  target?: string[] | null,
): string => {
  if (attribute === Attribute.AcquisitionChannel && operator === Operator.IsIn) {
    return getAcquisitionChannelValues(target);
  }

  if (
    attribute === Attribute.RestrictionVerification &&
    (operator === Operator.IsIn || operator === Operator.IsNotIn) &&
    target?.length
  ) {
    const titleByOperator = {
      [Operator.IsIn]: 'Is',
      [Operator.IsNotIn]: 'Is Not',
    };

    const verified =
      target && target.length > 1
        ? target.map((t) => categoryRestrictionTitle[t as Category_Restrictions] ?? t).join(' or ')
        : categoryRestrictionTitle[target[0] as Category_Restrictions] ?? target[0];

    return `${titleByOperator[operator]} ${verified} verified`;
  }

  if (attribute !== Attribute.AcquisitionChannel && operator === Operator.IsTrue) {
    return 'Yes';
  }

  if (attribute === Attribute.HasAch && operator === Operator.IsEqualTo) {
    return (target?.[0] as NonNullable<FuelAndConvenienceProfileValues>['hasACH']) === 'true'
      ? 'True'
      : 'False';
  }

  return '';
};

const prepareFuelAndConvenienceProfile: PrepareFuelAndConvenienceProfileAttribute = (criteria) => {
  return criteria
    .map(({ attribute, operator, target }) => {
      const label = attributeMap[attribute];
      const value = getAttributeValue(attribute, operator, target);

      if (!label || !value) return null;

      return { label, value };
    })
    .filter((item): item is { label: string; value: string } => !!item);
};

type Attributes = Omit<
  GroupAudienceTargetingAttributesByType,
  | 'purchases'
  | 'productInterests'
  | 'qualifiedEvents'
  | 'employeeTargetingAttributes'
  | 'combinedAudiences'
> & {
  purchases: Purchase[];
  surveyItems: Survey[];
  segments: Segment[];
  fulfillments: Fulfillment[];
  offerActivations: OfferActivation[];
  walletSteeringItems: WalletSteeringAttribute[];
  productInterests: ProductInterestsAttribute | null;
  qualifiedEvents: QualifiedEventAttribute[];
  employeeTargetingAttributes: EmployeeTargetingAttributes | null;
  combinedAudiences: CombinedAudiencesAttribute | null;
};

export const prepareAudienceAttributes = (attributes: Attributes): TargetingAttributes => {
  const ageWithin = attributes.ageWithin?.length ? attributes.ageWithin[0] : null;
  const fuelAndConvenienceProfile =
    attributes.fuelAndConvenienceProfile?.length &&
    attributes.fuelAndConvenienceProfile[0].criteria?.length
      ? prepareFuelAndConvenienceProfile(attributes.fuelAndConvenienceProfile[0].criteria)
      : [];

  const homeStore = attributes?.homeStore?.length > 0 ? attributes.homeStore[0] : null;

  const activeSubBrandCriteria = attributes.fuelAndConvenienceProfile?.[0]?.criteria?.find(
    (c) => c.attribute === Attribute.ActiveSubBrand && c.target?.length,
  );

  return {
    ageWithin,
    birthday: attributes.birthday?.length ? attributes.birthday[0] : null,
    purchases: attributes.purchases,
    segments: attributes.segments,
    fuelAndConvenienceProfile,
    fulfillments: attributes.fulfillments,
    offerActivations: attributes.offerActivations,
    homeStore,
    walletSteeringItems: attributes.walletSteeringItems,
    productInterests: attributes.productInterests,
    qualifiedEvents: attributes.qualifiedEvents,
    lastVisit: attributes.lastVisit,
    pointBalance: attributes.pointBalance,
    membershipCreated: attributes.membershipCreated,
    employeeTargetingAttributes: attributes.employeeTargetingAttributes,
    surveyItems: attributes.surveyItems,
    combinedAudiences: attributes.combinedAudiences,
    activeSubBrandUuids: activeSubBrandCriteria?.target ?? undefined,
    externalIdentityVerification: attributes.externalIdentityVerification,
    communicationPreference: attributes.communicationPreference,
  };
};
